import { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Funnel, X, ArrowCounterClockwise } from '@phosphor-icons/react';

export const DEFAULT_FILTERS = {
  priority: 'all',
  slaStatus: 'all',
  warrantyStatus: 'all',
  dateRange: 'all',
  sortBy: 'sla_urgency',
};

export const AgentTicketFilterPopover = ({ filters, setFilters }) => {
  const [open, setOpen] = useState(false);

  const activeCount = Object.entries(filters).filter(([key, val]) => {
    if (key === 'sortBy') return val !== DEFAULT_FILTERS.sortBy;
    return val !== 'all';
  }).length;

  const handleChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleReset = () => {
    setFilters(DEFAULT_FILTERS);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant={activeCount > 0 ? "default" : "outline"} className="relative gap-2" data-testid="filter-btn">
          <Funnel size={18} weight={activeCount > 0 ? "fill" : "regular"} />
          <span>Filter</span>
          {activeCount > 0 && (
            <Badge className="ml-1 bg-white text-slate-900 font-extrabold h-5 px-1.5 min-w-[20px] rounded-full text-[11px] shadow-sm">
              {activeCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-80 p-5 bg-white border shadow-lg rounded-xl space-y-4 z-50" align="end">
        <div className="flex items-center justify-between pb-3 border-b border-slate-150">
          <div className="flex items-center gap-2 font-bold text-slate-900 text-sm">
            <Funnel size={16} className="text-primary" />
            <span>Filter & Sort Tickets</span>
          </div>
          {activeCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReset}
              className="h-7 text-xs text-slate-500 hover:text-slate-900 px-2"
            >
              <ArrowCounterClockwise size={12} className="mr-1" />
              Reset
            </Button>
          )}
        </div>

        {/* Priority Filter */}
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-slate-600">Priority</Label>
          <Select value={filters.priority} onValueChange={(val) => handleChange('priority', val)}>
            <SelectTrigger className="w-full bg-white h-9 text-xs border-slate-250">
              <SelectValue placeholder="All Priorities" />
            </SelectTrigger>
            <SelectContent className="bg-white z-50">
              <SelectItem value="all">All Priorities</SelectItem>
              <SelectItem value="urgent">🔴 Urgent</SelectItem>
              <SelectItem value="normal">🟡 Normal</SelectItem>
              <SelectItem value="low">🔵 Low</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* SLA Status Filter */}
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-slate-600">SLA Status</Label>
          <Select value={filters.slaStatus} onValueChange={(val) => handleChange('slaStatus', val)}>
            <SelectTrigger className="w-full bg-white h-9 text-xs border-slate-250">
              <SelectValue placeholder="All SLA States" />
            </SelectTrigger>
            <SelectContent className="bg-white z-50">
              <SelectItem value="all">All SLA States</SelectItem>
              <SelectItem value="breached">⚠️ Breached / Expired</SelectItem>
              <SelectItem value="near_breach">⏱️ Near Breach (&lt; 1 Hour)</SelectItem>
              <SelectItem value="on_track">✅ On Track</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Warranty Status Filter */}
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-slate-600">Warranty Status</Label>
          <Select value={filters.warrantyStatus} onValueChange={(val) => handleChange('warrantyStatus', val)}>
            <SelectTrigger className="w-full bg-white h-9 text-xs border-slate-250">
              <SelectValue placeholder="All Warranty States" />
            </SelectTrigger>
            <SelectContent className="bg-white z-50">
              <SelectItem value="all">All Warranty States</SelectItem>
              <SelectItem value="active">Active Warranty</SelectItem>
              <SelectItem value="expired">Expired Warranty</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Date Range Filter */}
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-slate-600">Created Date</Label>
          <Select value={filters.dateRange} onValueChange={(val) => handleChange('dateRange', val)}>
            <SelectTrigger className="w-full bg-white h-9 text-xs border-slate-250">
              <SelectValue placeholder="All Time" />
            </SelectTrigger>
            <SelectContent className="bg-white z-50">
              <SelectItem value="all">All Time</SelectItem>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="last_7_days">Last 7 Days</SelectItem>
              <SelectItem value="last_30_days">Last 30 Days</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Sort By Option */}
        <div className="space-y-1.5 pt-2 border-t border-slate-150">
          <Label className="text-xs font-semibold text-slate-600">Sort Tickets By</Label>
          <Select value={filters.sortBy} onValueChange={(val) => handleChange('sortBy', val)}>
            <SelectTrigger className="w-full bg-white h-9 text-xs border-slate-250">
              <SelectValue placeholder="Sort Order" />
            </SelectTrigger>
            <SelectContent className="bg-white z-50">
              <SelectItem value="sla_urgency">SLA Urgency (Default)</SelectItem>
              <SelectItem value="priority_desc">Priority (Urgent → Low)</SelectItem>
              <SelectItem value="created_desc">Created Date (Newest First)</SelectItem>
              <SelectItem value="created_asc">Created Date (Oldest First)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="pt-2 flex justify-end">
          <Button size="sm" onClick={() => setOpen(false)} className="w-full h-8 text-xs font-bold">
            Apply Filters
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const ActiveFilterChips = ({ filters, setFilters }) => {
  const activeChips = [];

  if (filters.priority !== 'all') {
    activeChips.push({ key: 'priority', label: `Priority: ${filters.priority}` });
  }
  if (filters.slaStatus !== 'all') {
    const labels = { breached: 'SLA: Breached', near_breach: 'SLA: Near Breach', on_track: 'SLA: On Track' };
    activeChips.push({ key: 'slaStatus', label: labels[filters.slaStatus] || filters.slaStatus });
  }
  if (filters.warrantyStatus !== 'all') {
    activeChips.push({ key: 'warrantyStatus', label: `Warranty: ${filters.warrantyStatus}` });
  }
  if (filters.dateRange !== 'all') {
    const labels = { today: 'Created: Today', last_7_days: 'Created: Last 7 Days', last_30_days: 'Created: Last 30 Days' };
    activeChips.push({ key: 'dateRange', label: labels[filters.dateRange] || filters.dateRange });
  }
  if (filters.sortBy !== DEFAULT_FILTERS.sortBy) {
    const labels = {
      priority_desc: 'Sort: Priority',
      created_desc: 'Sort: Newest First',
      created_asc: 'Sort: Oldest First',
    };
    activeChips.push({ key: 'sortBy', label: labels[filters.sortBy] || filters.sortBy });
  }

  if (activeChips.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 mb-4 animate-fade-in">
      <span className="text-xs font-semibold text-slate-500">Active Filters:</span>
      {activeChips.map((chip) => (
        <Badge
          key={chip.key}
          variant="secondary"
          className="bg-slate-100 text-slate-800 border border-slate-200 hover:bg-slate-200 px-2.5 py-1 text-xs gap-1.5 flex items-center"
        >
          {chip.label}
          <X
            size={12}
            className="cursor-pointer hover:text-red-600 transition-colors"
            onClick={() => setFilters((prev) => ({ ...prev, [chip.key]: DEFAULT_FILTERS[chip.key] }))}
          />
        </Badge>
      ))}
      <button
        onClick={() => setFilters(DEFAULT_FILTERS)}
        className="text-xs text-primary font-bold hover:underline ml-1"
      >
        Clear All
      </button>
    </div>
  );
};
